import {
  IInvalidField,
  IPolicy,
  PolicyType,
} from "shared";
import {
  useMemo,
  useState,
  useEffect,
} from "react";

import { useAccountingResources } from "../../../../hooks/useAccountingResources";
import { Transition } from "../../../../../common/Atoms/Transition";
import { MultiSelect } from "../../../../../common/Atoms/MultiSelect";
import { Select } from "../../../../../common/Atoms/Select";
import { fieldIsValid } from "../../../../lib/helper";
import { Grid } from "../../../../../common/Atoms/Grid";
import { GridItem } from "../../../../../common/Atoms/GridItem";

interface IAccountMultiSelectorProps {
  policy: IPolicy;
  invalids: IInvalidField[];
  handleChange: (obj: {
    name: keyof IPolicy;
    value: string;
  }) => void;
}

type FilterMode = `none` | `exclude` | `include`;

export function AccountMultiSelector({
  policy,
  handleChange,
  invalids,
}: IAccountMultiSelectorProps) {
  const { data: resources, isLoading } = useAccountingResources();

  // Determine initial filter mode from the policy value
  const initialFilterMode = useMemo((): FilterMode => {
    if (!policy.exclude_account_lines) return `none`;

    return policy.exclude_account_lines.startsWith(`INCLUDE_ONLY:`) ? `include` : `exclude`;
  }, [policy.exclude_account_lines]);

  // Track the current filter mode with local state
  const [currentFilterMode, setCurrentFilterMode] = useState<FilterMode>(initialFilterMode);

  // Update local state when policy changes externally
  useEffect(() => {
    setCurrentFilterMode(initialFilterMode);
  }, [initialFilterMode]);

  const descriptionText = useMemo(() => {
    if (currentFilterMode === `include`) {
      return policy.policy_type === PolicyType.LATE_FEE || policy.policy_type === PolicyType.STATEMENT_LATE_FEE ?
        `Include only these invoice lines when calculating percentages, if no invoice lines are included, this policy will not apply to the invoice` :
        `If no invoice lines are included, this policy will not apply to the invoice`;
    }
    if (currentFilterMode === `exclude`) {
      return policy.policy_type === PolicyType.LATE_FEE || policy.policy_type === PolicyType.STATEMENT_LATE_FEE ?
        `Exclude these invoice lines when calculating percentages, if all invoice lines are excluded, this policy will not apply to the invoice` :
        `If all invoice lines are excluded, this policy will not apply to the invoice`;
    }

    return `Select accounts to include or exclude from calculations`;
  }, [currentFilterMode, policy.policy_type]);

  const selectedAccounts = useMemo(() => {
    if (!policy.exclude_account_lines) return [];

    // Remove INCLUDE_ONLY: prefix if present
    const cleanValue = policy.exclude_account_lines.startsWith(`INCLUDE_ONLY:`)
      ? policy.exclude_account_lines.substring(13) // Remove 'INCLUDE_ONLY:' prefix
      : policy.exclude_account_lines;

    return cleanValue.split(`,`).filter(account => account.trim().length > 0);
  }, [policy.exclude_account_lines]);

  const displayValue = useMemo(() => {
    if (isLoading) {
      return `Loading...`;
    }

    if (!selectedAccounts.length) {
      return `None Selected`;
    }

    return `${selectedAccounts.length} selected`;
  }, [selectedAccounts, isLoading]);

  const accounts = useMemo(() => {
    if (isLoading) {
      return [];
    }

    if (!resources?.accounts) {
      return [];
    }

    return resources.accounts.map(account => ({
      label: account.label,
      value: account.code,
    }));
  }, [resources?.accounts, isLoading]);

  const filterModeOptions = [
    { value: `none`, label: `Select include or exclude?` },
    { value: `exclude`, label: `Exclude` },
    { value: `include`, label: `Include` },
  ];

  function handleFilterModeChange(mode: FilterMode) {
    setCurrentFilterMode(mode);

    if (mode === `none`) {
      // Clear the field when going back to none
      handleChange({
        value: null,
        name: `exclude_account_lines`,
      });
    }
    else if (policy.exclude_account_lines && (mode === `include` || mode === `exclude`)) {
      // Switch between include/exclude modes while preserving selected accounts
      const cleanValue = policy.exclude_account_lines.startsWith(`INCLUDE_ONLY:`)
        ? policy.exclude_account_lines.substring(13)
        : policy.exclude_account_lines;

      const newValue = mode === `include` ? `INCLUDE_ONLY:${cleanValue}` : cleanValue;

      handleChange({
        value: newValue,
        name: `exclude_account_lines`,
      });
    }
  }

  function handleAccountsChange(value: string[]) {
    const cleanedValues = value.filter(a => a);
    let newValue = cleanedValues.length ? cleanedValues.join(`,`) : null;

    // Add INCLUDE_ONLY prefix if in include mode
    if (newValue && currentFilterMode === `include`) {
      newValue = `INCLUDE_ONLY:${newValue}`;
    }

    handleChange({
      value: newValue,
      name: `exclude_account_lines`,
    });
  }

  return (
    <>
      <Grid
        cols={ 3 }
      >
        <GridItem span={ 3 }>
          <Select
            label={ `${currentFilterMode === `include` ? `Include` : `Exclude`} invoice lines` }
            description={ descriptionText }
            options={ filterModeOptions }
            selected={ currentFilterMode }
            onChange={ ({ value }) => handleFilterModeChange(value as FilterMode) }
          />
        </GridItem>

        <GridItem span={ 3 }>
          <Transition
            show={ currentFilterMode !== `none` }
            speed={ `slow` }
          >
            <div>
              <MultiSelect
                options={ accounts }
                onChange={ handleAccountsChange }
                invalid={ fieldIsValid(`exclude_account_lines`, invalids) }
                selected={ selectedAccounts }
                display={ displayValue }
              />
            </div>
          </Transition>
        </GridItem>
      </Grid>
    </>
  );
}
